// ── Profile page — edit user preferences ─────────────────────────────
// Language / country / units / theme selector. Reads and writes
// `/api/v1/users/me/preferences`, then re-applies i18n + theme without
// a page reload.

import { setLanguage, t } from '../shared/i18n.ts';
import {
  fetchCurrentUser,
  updatePreferences,
  applyTheme,
  persistUnits,
  UnauthorizedError,
  type Theme,
} from '../shared/preferences.ts';
import type { UnitSystem } from '../shared/units.ts';

// Keep in sync with SUPPORTED_LOCALES in config/initializers/locales.rb.
// Listed alphabetically so the profile dropdown renders in a stable order
// regardless of locale-JSON load timing.
const SUPPORTED_LANGS = [
  'ar', 'bg', 'cs', 'de', 'el', 'en', 'es', 'fr', 'he', 'hr',
  'hu', 'it', 'nl', 'pl', 'pt', 'ro', 'sk', 'sv', 'tr', 'uk',
];

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function redirectToLogin() {
  location.replace(`/login?next=${encodeURIComponent(location.pathname)}`);
}

async function loadForm() {
  let user: Awaited<ReturnType<typeof fetchCurrentUser>>;
  try {
    user = await fetchCurrentUser();
  } catch (err) {
    if (err instanceof UnauthorizedError) {
      redirectToLogin();
      return;
    }
    const status = byId<HTMLElement>('prefStatus');
    if (status) status.textContent = t('profile.errorLoad');
    return;
  }
  if (!user) {
    redirectToLogin();
    return;
  }

  const prefs = user.preferences;
  const langSel = byId<HTMLSelectElement>('prefLanguage');
  const countryInput = byId<HTMLInputElement>('prefCountry');
  const unitsSel = byId<HTMLSelectElement>('prefUnits');
  const themeSel = byId<HTMLSelectElement>('prefTheme');

  if (langSel) langSel.value = prefs.language || 'en';
  if (countryInput) countryInput.value = prefs.country || '';
  if (unitsSel) unitsSel.value = prefs.units || 'metric';
  if (themeSel) themeSel.value = prefs.theme || 'auto';
}

async function onSave(event: SubmitEvent) {
  event.preventDefault();
  const status = byId<HTMLElement>('prefStatus');
  if (status) status.textContent = '';

  const langSel = byId<HTMLSelectElement>('prefLanguage');
  const countryInput = byId<HTMLInputElement>('prefCountry');
  const unitsSel = byId<HTMLSelectElement>('prefUnits');
  const themeSel = byId<HTMLSelectElement>('prefTheme');

  const country = (countryInput?.value || '').trim().toUpperCase();
  const payload: Record<string, unknown> = {
    language: langSel?.value,
    units: unitsSel?.value as UnitSystem,
    theme: themeSel?.value as Theme,
  };
  if (country.length === 0) {
    payload.country = null;
  } else if (/^[A-Z]{2}$/.test(country)) {
    payload.country = country;
    if (countryInput) countryInput.value = country;
  } else {
    if (status) status.textContent = t('profile.errorCountryFormat');
    return;
  }

  try {
    const result = await updatePreferences(payload);
    if (!result) throw new Error(t('profile.errorUnauthorized'));

    const prefs = result.preferences;
    applyTheme(prefs.theme);
    persistUnits(prefs.units);
    await setLanguage(prefs.language);
    if (status) status.textContent = t('profile.saved');
  } catch (err) {
    if (status) status.textContent = err instanceof Error ? err.message : t('profile.errorSave');
  }
}

function populateLanguages() {
  const sel = byId<HTMLSelectElement>('prefLanguage');
  if (!sel || sel.options.length > 0) return;
  for (const code of SUPPORTED_LANGS) {
    const opt = document.createElement('option');
    opt.value = code;
    opt.textContent = code.toUpperCase();
    sel.appendChild(opt);
  }
}

export function initProfilePage() {
  populateLanguages();
  const form = byId<HTMLFormElement>('prefForm');
  if (!form) return;
  form.addEventListener('submit', onSave);
  void loadForm();
}

